/* eslint-disable react-refresh/only-export-components -- variant helper is a stable public styling API */
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-[var(--radius-control)] text-sm font-medium transition-colors focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-[var(--button-primary-bg)] text-[var(--button-primary-fg)] hover:bg-[var(--color-action-hover)]',
        primary:
          'bg-[var(--button-primary-bg)] text-[var(--button-primary-fg)] hover:bg-[var(--color-action-hover)]',
        signal:
          'bg-[var(--button-signal-bg)] text-[var(--color-porcelain)] hover:bg-signal-600',
        success:
          'bg-[var(--color-success)] text-[var(--color-porcelain)] brightness-100 hover:brightness-90',
        destructive:
          'bg-[var(--color-danger)] text-[var(--color-porcelain)] brightness-100 hover:brightness-90',
        outline:
          'border border-[var(--input-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]',
        outlineDestructive:
          'border border-[var(--color-danger)] bg-[var(--color-surface)] text-[var(--color-danger)] hover:bg-[color-mix(in_srgb,var(--color-danger)_8%,transparent)]',
        ghost:
          'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]',
        link:
          'text-[var(--color-text)] underline-offset-4 decoration-[var(--color-signal)] hover:underline',
      },
      size: {
        // Mobile-first sizing — base height ≥40px (iOS 44pt approx). Desktop
        // overrides via sm: prefix bring back compact density. Touch target
        // standard: Apple HIG 44pt / Material 48dp minimum.
        default: 'min-h-[44px] sm:min-h-[36px] sm:h-9 px-4 py-2',
        sm: 'min-h-[40px] sm:min-h-[32px] sm:h-8 px-3 text-xs',
        lg: 'min-h-[48px] sm:min-h-[40px] sm:h-10 px-6',
        icon: 'min-h-[44px] min-w-[44px] sm:min-h-[36px] sm:min-w-[36px] sm:h-9 sm:w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { buttonVariants }
